import React, { useContext, createContext } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { usePanel } from 'contexts/panelContext';
import { Pair } from 'types/Pair';
import PageMissing from 'components/PageMissing';
import { PageLoading } from 'components/PageLoading';
import { Goals } from 'types/Goal';
import useGoals from 'hooks/useGoals';
import { Application } from 'types/Application';
import { userPanelApi } from 'api';
import { ContractType } from 'types/Contract';
import { ErrorContainer } from 'components/_new/ErrorContainer/ErrorContainer';

type PairContextType = {
	pair: Pair;
	application: Application;
	goals?: Goals;
	contract: ContractType;
	path: string;
};

export const PairContext = createContext<PairContextType>({} as PairContextType);

export const PairProvider = () => {
	const { pairId } = useParams();
	const { t } = useTranslation();

	const {
		userPairs,
		userPairsLoading,
		userPairsFetched,
		currentApplicationData,
		currentProgramMembership,
		panelScopePath,
	} = usePanel();

	const pair = (userPairs || []).find(({ id }) => id === Number(pairId));
	const {
		data: contractData,
		isLoading: contractLoading,
		isError,
		refetch,
		error,
	} = useQuery(['contract', pairId], () => userPanelApi.getContract(Number(pair?.id), currentProgramMembership.id), {
		enabled: Boolean(pair?.id),
	});

	const { fetchedGoals } = useGoals(Number(pairId));

	if (!userPairsFetched || userPairsLoading || contractLoading) {
		return <PageLoading text={t('userPanel.pairing.loadingPairMessage')} />;
	}

	if (userPairsFetched && !pair) {
		return <PageMissing />;
	}

	if (isError) {
		return <ErrorContainer action={refetch} error={error} />;
	}

	if (!pair || !contractData) {
		return <ErrorContainer />;
	}

	const path = `${panelScopePath}/pair/${pair.id}`;

	return (
		<PairContext.Provider
			value={{
				pair,
				application:
					currentApplicationData.applicationRole === 'mentee'
						? pair.mentorApplication
						: pair.menteeApplication,
				goals: fetchedGoals,
				contract: contractData,
				path,
			}}
		>
			<Outlet />
		</PairContext.Provider>
	);
};

export const usePair = () => useContext(PairContext);
